import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Select, Textarea,
  FormControl, FormLabel, Box, Text, Flex, Divider, useToast, IconButton,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import api from '../api';

export default function ProspectsModal({ isOpen, onClose, onRefresh, editData = null, campaignId = null, campaignName = null }) {
  const [leadSources, setLeadSources] = useState([]);
  const [engStatuses, setEngStatuses] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [referralContact, setReferralContact] = useState(null);
  const [referralSearch, setReferralSearch] = useState('');
  const [referralResults, setReferralResults] = useState([]);
  const toast = useToast();

  const emptyForm = {
    company_name: '', website: '', email: '', phone: '', address: '',
    business_summary: '', business_description: '', outreach_ideas: '', lead_query: '',
    lead_source_id: '', engagement_status_id: '',
    contact_name: '', contact_email: '', contact_phone: '', contact_warm_info: '',
    company_socials: [], contact_socials: [],
  };
  const [form, setForm] = useState(emptyForm);

  const fetchLookups = useCallback(async () => {
    try {
      const [ls, es, nets] = await Promise.all([
        api.get('/lookups/lead-sources'), api.get('/lookups/engagement-statuses'),
        api.get('/social/networks'),
      ]);
      setLeadSources(ls.data); setEngStatuses(es.data); setNetworks(nets.data);
      if (!editData) {
        const coldSource = ls.data.find(l => l.name === 'Prospecting - Data - Cold');
        const coldStatus = es.data.find(e => e.name === 'Cold');
        setForm(p => ({ ...p, lead_source_id: coldSource?.id || '', engagement_status_id: coldStatus?.id || '' }));
      }
    } catch(e) {}
  }, [editData]);

  useEffect(() => {
    if (isOpen) {
      fetchLookups();
      if (editData) {
        setForm({ ...emptyForm, ...editData, company_socials: [], contact_socials: [] });
      }
    }
  }, [isOpen, fetchLookups]); // eslint-disable-line

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const addSocial = (type) => {
    const key = type === 'company' ? 'company_socials' : 'contact_socials';
    setForm(p => ({ ...p, [key]: [...p[key], { social_network_id: '', handle: '' }] }));
  };
  const updateSocial = (type, idx, field, val) => {
    const key = type === 'company' ? 'company_socials' : 'contact_socials';
    setForm(p => ({ ...p, [key]: p[key].map((s, i) => i === idx ? { ...s, [field]: val } : s) }));
  };
  const removeSocial = (type, idx) => {
    const key = type === 'company' ? 'company_socials' : 'contact_socials';
    setForm(p => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));
  };

  const handleSave = async (andAnother = false) => {
    if (!form.company_name) { toast({ title: 'Company name required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      if (campaignId) {
        // Campaign mode: find/create company then add as prospect entry
        // Build rich notes from form fields
        const notes = [
          form.business_summary && `Proposition: ${form.business_summary}`,
          form.business_description && `About them: ${form.business_description}`,
          form.lead_query && `Their requirement: ${form.lead_query}`,
          form.outreach_ideas && `How we can help: ${form.outreach_ideas}`,
          form.contact_name && `Contact: ${form.contact_name}${form.contact_email ? ` <${form.contact_email}>` : ''}${form.contact_phone ? ` ${form.contact_phone}` : ''}`,
          form.contact_warm_info && `Warm info: ${form.contact_warm_info}`,
        ].filter(Boolean).join('\n');

        // Try to find existing company by name or create new entry with company_name
        let companyId = null;
        try {
          const { data: cos } = await api.get(`/companies?search=${encodeURIComponent(form.company_name)}&limit=3`);
          const match = cos.find(c => c.name.toLowerCase() === form.company_name.toLowerCase());
          if (match) {
            companyId = match.id;
          } else {
            const { data: newCo } = await api.post('/companies', {
              name: form.company_name, website: form.website || null,
              email: form.email || null, phone: form.phone || null, address: form.address || null,
              company_type: 'client',
            });
            companyId = newCo.id;
          }
        } catch(e) {}

        const { data: entry } = await api.post('/prospects/entries', {
          campaign_id: campaignId,
          company_id: companyId,
          notes: notes || null,
        });

        // Post lead_query as an inbound communication
        if (form.lead_query && entry?.project_id) {
          await api.post('/communications', {
            project_id: entry.project_id,
            company_id: companyId,
            direction: 'inbound',
            method: 'other',
            subject: 'Lead Query / Requirement',
            body: form.lead_query,
            comm_date: new Date().toISOString().split('T')[0],
          }).catch(() => {});
        }

        // Post outreach_ideas as a note
        if (form.outreach_ideas && entry?.project_id) {
          await api.post('/notes', {
            project_id: entry.project_id,
            subject: 'How We Can Help',
            body: form.outreach_ideas,
            note_date: new Date().toISOString().split('T')[0],
          }).catch(() => {});
        }
      } else {
        await api.post('/prospects', form);
      }
      toast({ title: 'Prospect added', status: 'success', duration: 1500 });
      if (onRefresh) onRefresh();
        setReferralContact(null); setReferralSearch(''); setReferralResults([]);
      if (andAnother) {
        const coldSource = leadSources.find(l => l.name === 'Prospecting - Data - Cold');
        const coldStatus = engStatuses.find(e => e.name === 'Cold');
        setForm({ ...emptyForm, lead_source_id: coldSource?.id || '', engagement_status_id: coldStatus?.id || '',
          company_socials: [], contact_socials: [] });
      } else {
        onClose();
      }
      // Record referral if a referral contact was selected
      if (referralContact && campaignId) {
        try {
          await api.post('/referrals', {
            contact_id: referralContact.id,
            project_id: null, // will be linked once prospect converts
            notes: `Referred prospect: ${form.company_name}`,
            referred_date: new Date().toISOString().split('T')[0],
          });
        } catch(e) { /* non-fatal */ }
      }
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh">
        <ModalHeader fontSize="md">{editData ? 'Edit Prospect' : campaignName ? `Add Prospect — ${campaignName}` : 'Add Prospect'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color="gray.500" w="100%" letterSpacing="0.08em">Company</Text>
            <HStack w="100%" spacing={2}>
              <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Company Name *</FormLabel>
                <Input size="sm" value={form.company_name} onChange={f('company_name')} autoFocus /></FormControl>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Website</FormLabel>
                <Input size="sm" value={form.website} onChange={f('website')} placeholder="https://" /></FormControl>
            </HStack>
            <HStack w="100%" spacing={2}>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>General Email</FormLabel>
                <Input size="sm" value={form.email} onChange={f('email')} /></FormControl>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>General Phone</FormLabel>
                <Input size="sm" value={form.phone} onChange={f('phone')} /></FormControl>
            </HStack>
            <FormControl><FormLabel fontSize="xs" mb={0.5}>Address</FormLabel>
              <Textarea size="sm" rows={2} value={form.address} onChange={f('address')} /></FormControl>

            <HStack w="100%" spacing={2}>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Lead Source</FormLabel>
                <Select size="sm" value={form.lead_source_id} onChange={f('lead_source_id')}>
                  <option value="">—</option>
                  {leadSources.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </Select></FormControl>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Engagement Status</FormLabel>
                <Select size="sm" value={form.engagement_status_id} onChange={f('engagement_status_id')}>
                  <option value="">—</option>
                  {engStatuses.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </Select></FormControl>
            </HStack>

            {/* Company Socials */}
            <Box w="100%">
              <Flex justify="space-between" align="center" mb={1}>
                <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color="gray.400" letterSpacing="0.08em">Company Socials</Text>
                <IconButton icon={<FiPlus />} size="xs" variant="ghost" colorScheme="blue"
                  onClick={() => addSocial('company')} aria-label="Add social" />
              </Flex>
              {form.company_socials.map((s, i) => (
                <HStack key={i} spacing={1} mb={1}>
                  <Select size="xs" value={s.social_network_id} flex={1}
                    onChange={e => updateSocial('company', i, 'social_network_id', e.target.value)}>
                    <option value="">Network...</option>
                    {networks.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                  </Select>
                  <Input size="xs" flex={2} placeholder="Handle / URL" value={s.handle}
                    onChange={e => updateSocial('company', i, 'handle', e.target.value)} />
                  <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                    onClick={() => removeSocial('company', i)} aria-label="Remove" />
                </HStack>
              ))}
            </Box>

            <Divider />
            <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color="gray.500" w="100%" letterSpacing="0.08em">Our Proposition</Text>
            <FormControl><FormLabel fontSize="xs" mb={0.5}>What We Can Offer (one-line summary)</FormLabel>
              <Input size="sm" value={form.business_summary} onChange={f('business_summary')} placeholder="e.g. Video production and social media packages" /></FormControl>
            <FormControl><FormLabel fontSize="xs" mb={0.5}>About Their Business (saved as note)</FormLabel>
              <Textarea size="sm" rows={2} value={form.business_description} onChange={f('business_description')} /></FormControl>

            <Divider />
            <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color="orange.500" w="100%" letterSpacing="0.08em">Lead Query</Text>
            <FormControl>
              <FormLabel fontSize="xs" mb={0.5}>Their Requirement / Lead Query</FormLabel>
              <Textarea size="sm" rows={3} value={form.lead_query} onChange={f('lead_query')}
                placeholder="What are they looking for? What did they enquire about? Their brief or requirements..."
                borderColor="orange.200" _focus={{ borderColor: 'orange.400' }} />
              <Text fontSize="9px" color="orange.400" mt={0.5}>Saved as an inbound communication on the prospect record</Text>
            </FormControl>

            <FormControl><FormLabel fontSize="xs" mb={0.5}>{'How We Can Help — Our Services & Approach'}</FormLabel>
              <Textarea size="sm" rows={2} value={form.outreach_ideas} onChange={f('outreach_ideas')}
                placeholder="How can we address their needs? Which services apply?"
                borderColor="teal.200" _focus={{ borderColor: 'teal.400' }} />
              <Text fontSize="9px" color="teal.400" mt={0.5}>Saved as a note on the prospect record</Text>
            </FormControl>

            <Divider />
            <Box w="100%">
              <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color="blue.500" w="100%" letterSpacing="0.08em" mb={2}>Referral (optional)</Text>
              <Text fontSize="10px" color="gray.400" mb={2}>Was this prospect referred by someone? Search and link them.</Text>
              <Box position="relative">
                <Input size="sm" placeholder="Search contacts for referrer..."
                  value={referralSearch}
                  onChange={async e => {
                    setReferralSearch(e.target.value);
                    if (e.target.value.length >= 2) {
                      try {
                        const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(e.target.value)}`);
                        setReferralResults(data || []);
                      } catch(err) { setReferralResults([]); }
                    } else { setReferralResults([]); }
                  }} />
                {referralResults.length > 0 && (
                  <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px"
                    borderColor="blue.200" borderRadius="md" shadow="md" zIndex={20} maxH="150px" overflowY="auto">
                    {referralResults.map(c => (
                      <Box key={c.id} px={3} py={2} cursor="pointer" fontSize="12px" _hover={{bg:'blue.50'}}
                        onClick={() => { setReferralContact(c); setReferralSearch(`${c.first_name} ${c.last_name||''}`); setReferralResults([]); }}>
                        <Text fontWeight="600">{c.first_name} {c.last_name}</Text>
                        {c.company_name && <Text fontSize="10px" color="gray.400">{c.company_name}</Text>}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              {referralContact && (
                <HStack mt={1}>
                  <Badge colorScheme="blue">👤 {referralContact.first_name} {referralContact.last_name}</Badge>
                  <Button size="xs" variant="ghost" colorScheme="red" onClick={() => { setReferralContact(null); setReferralSearch(''); }}>Remove</Button>
                </HStack>
              )}
            </Box>
            <Divider />
            <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color="gray.500" w="100%" letterSpacing="0.08em">Contact (optional)</Text>
            <HStack w="100%" spacing={2}>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Contact Name</FormLabel>
                <Input size="sm" value={form.contact_name} onChange={f('contact_name')} placeholder="First Last" /></FormControl>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Contact Email</FormLabel>
                <Input size="sm" value={form.contact_email} onChange={f('contact_email')} /></FormControl>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Contact Phone</FormLabel>
                <Input size="sm" value={form.contact_phone} onChange={f('contact_phone')} /></FormControl>
            </HStack>
            <FormControl><FormLabel fontSize="xs" mb={0.5}>Warm Contact Info / Notes</FormLabel>
              <Textarea size="sm" rows={2} value={form.contact_warm_info} onChange={f('contact_warm_info')}
                placeholder="How did we find them, any mutual connections, etc." /></FormControl>

            {/* Contact Socials */}
            <Box w="100%">
              <Flex justify="space-between" align="center" mb={1}>
                <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color="gray.400" letterSpacing="0.08em">Contact Socials</Text>
                <IconButton icon={<FiPlus />} size="xs" variant="ghost" colorScheme="blue"
                  onClick={() => addSocial('contact')} aria-label="Add social" />
              </Flex>
              {form.contact_socials.map((s, i) => (
                <HStack key={i} spacing={1} mb={1}>
                  <Select size="xs" value={s.social_network_id} flex={1}
                    onChange={e => updateSocial('contact', i, 'social_network_id', e.target.value)}>
                    <option value="">Network...</option>
                    {networks.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                  </Select>
                  <Input size="xs" flex={2} placeholder="Handle / URL" value={s.handle}
                    onChange={e => updateSocial('contact', i, 'handle', e.target.value)} />
                  <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                    onClick={() => removeSocial('contact', i)} aria-label="Remove" />
                </HStack>
              ))}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr="auto" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="outline" colorScheme={campaignId ? 'purple' : 'blue'} mr={2}
            onClick={() => handleSave(false)} isLoading={saving}>
            {campaignId ? 'Add to Campaign' : 'Save and Exit'}
          </Button>
          <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
            onClick={() => handleSave(true)} isLoading={saving}>
            {campaignId ? 'Add & Add Another' : 'Save and Add Another'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
